import { SubscriptionTier } from '../models/Subscription';
import { partnerAccessRankOfTier } from './MembershipTierAccess';

/**
 * A handful of partners exist twice in the partner database: once as an Edge row
 * (`membershipType: edge`) and once as a Service Nation row, usually Roundtable.
 * The pair predates cumulative partner access - the list endpoint used to serve an
 * Edge member only Edge rows and a paying member only non-Edge rows, so a partner
 * that both platforms sell needed one row per side. Now that the frontend asks for
 * every active partner and gates each one on the member's own rung, both copies
 * come back and the wrong one has to be dropped.
 *
 * This list is a stopgap agreed with product on SN-996, not a permanent fixture:
 * the real fix is to merge each pair in the database. Product has confirmed no
 * further duplicates will be created, so the list is closed.
 *
 * Pairs are keyed on `code` because it is unique and stable, unlike `name`, which
 * differs between the two rows for most of these (Behr / Behr Paint Company). The
 * codes follow no single convention - note `SBR`/`SBRSRT` against the usual
 * `-SRT` suffix - so they cannot be derived and are spelled out.
 *
 * Rows whose Edge copy is currently inactive are still listed: `status` is set per
 * environment, so a row that is inactive in production may be active in dev or
 * staging. Duplicates product resolved by deactivating one copy outright
 * (PlumbMaster, Rheem, Ruud, Lennox) are deliberately absent.
 */
export const DUPLICATE_PARTNER_CODES: ReadonlyArray<readonly [string, string]> = [
  ['CO', 'CO-SRT'], // Coast
  ['CW', 'CW-SRT'], // Capital Wraps
  ['EV', 'EV-SRT'], // EverConnect
  ['HDS', 'HDS-SRT'], // HD Supply
  ['SBR', 'SBRSRT'], // Sunbelt Rentals
  ['WS', 'WS-SRT'], // Winsupply
  ['WST', 'WST-SRT'], // WeSupplyTrades
  ['BR', 'BPC'], // Behr / Behr Paint Company
  ['RI', 'RAC'], // Rinnai / Rinnai America Corporation
  ['ST', 'STL'], // Spartan Tool / Spartan Tool, LLC
  ['hd', 'hd-SRT'], // HomeDepot / The Home Depot
];

type DuplicatePartner = {
  code: string;
  membershipType?: SubscriptionTier | null;
};

/**
 * The code of the copy a member on `viewerTier` should not be shown, for every
 * pair whose two copies are both present. A member on the Edge rung keeps the Edge
 * copy; everyone above it keeps the Service Nation copy, including a member whose
 * own rung sits between the two - Association takes the Roundtable copy rather
 * than falling back to Edge's (SN-996).
 *
 * `protectedCodes` is never hidden: a copy the member is already enrolled with has
 * to stay visible whichever rung they are on, otherwise upgrading makes an existing
 * enrollment disappear from Your Partners and 404s its profile page.
 */
function duplicateCodesToHide(
  partners: ReadonlyArray<DuplicatePartner>,
  viewerTier?: SubscriptionTier | null,
  protectedCodes: ReadonlySet<string> = new Set(),
): Set<string> {
  const byCode = new Map(partners.map((partner) => [partner.code, partner]));
  const viewerRank = partnerAccessRankOfTier(viewerTier);
  const hidden = new Set<string>();

  DUPLICATE_PARTNER_CODES.forEach(([edgeCode, serviceNationCode]) => {
    const edgeCopy = byCode.get(edgeCode);
    const serviceNationCopy = byCode.get(serviceNationCode);
    // Only one copy reached this member, so there is nothing to disambiguate.
    if (!edgeCopy || !serviceNationCopy) return;

    const edgeRank = partnerAccessRankOfTier(edgeCopy.membershipType);
    const codeToHide = viewerRank > edgeRank ? edgeCode : serviceNationCode;
    if (!protectedCodes.has(codeToHide)) hidden.add(codeToHide);
  });

  return hidden;
}

/**
 * Drops the duplicate copy a member on `viewerTier` should not see, leaving every
 * other partner and the original ordering untouched.
 *
 * Pass `protectedCodes` wherever the result also feeds a lookup by id rather than
 * only a browse list, so that a member enrolled with the copy this would otherwise
 * hide still sees their own enrollment.
 */
export function withoutDuplicatePartners<T extends DuplicatePartner>(
  partners: ReadonlyArray<T> | undefined,
  viewerTier?: SubscriptionTier | null,
  protectedCodes?: ReadonlySet<string>,
): T[] | undefined {
  if (!partners) return partners as undefined;
  const hidden = duplicateCodesToHide(partners, viewerTier, protectedCodes);
  if (hidden.size === 0) return partners as T[];
  return partners.filter((partner) => !hidden.has(partner.code));
}
